<script setup lang="ts">
	import NonAuthenticatedLayout from '@/layouts/NonAuthenticatedLayout.vue'
	import { computed, ref, watch } from 'vue'

	import FormOtp from './components/FormOtp.vue'
	import useVerifyEmail from './composables/useVerifyEmail'

	const params = new URLSearchParams(window.location.search)
	const decodedUrl = decodeURI(params.get('url') || '')

	const urlParts = computed(() => {
		const afterVerifyEmail = decodedUrl.split('verify-email/')[1] || ''
		const parts = afterVerifyEmail.split('/')

		return {
			id: parts[0] || '',
			hash: parts[1] || '',
			fullPath: afterVerifyEmail,
		}
	})

	const shouldVerify = computed(
		() => !!urlParts.value.id && !!urlParts.value.hash
	)

	const { data, isLoading, isFetching } = useVerifyEmail({
		id: urlParts.value.id,
		hash: urlParts.value.hash,
		queryOptions: {
			enabled: shouldVerify.value,
			refetchOnWindowFocus: false,
		},
	})

	const formOtpRef = ref<InstanceType<typeof FormOtp> | null>(null)

	watch(
		() => data.value?.status,
		(status) => {
			if (status === 204 && formOtpRef.value) {
				formOtpRef.value.startCountdown()
			}
		}
	)
</script>

<template>
	<NonAuthenticatedLayout>
		<div
			class="flex flex-col items-center justify-center min-h-screen p-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
		>
			<div class="w-full max-w-md">
				<!-- Card principal -->
				<div class="bg-white rounded-xl shadow-xl overflow-hidden">
					<!-- Header decorativo -->
					<div
						class="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 text-center"
					>
						<div
							class="mx-auto w-14 h-14 bg-white rounded-full flex items-center justify-center mb-2 shadow-lg"
						>
							<svg
								class="w-7 h-7 text-blue-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
								></path>
							</svg>
						</div>
						<h1 class="text-xl font-bold text-white">Verificación de Email</h1>
						<p class="text-blue-100 text-xs">Enlacebooking</p>
					</div>

					<!-- Contenido -->
					<div class="p-5">
						<!-- Loading state -->
						<div
							v-if="isLoading || isFetching"
							class="text-center py-6"
						>
							<div class="relative inline-flex">
								<div
									class="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"
								></div>
								<div class="absolute inset-0 flex items-center justify-center">
									<svg
										class="w-6 h-6 text-blue-600"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M5 13l4 4L19 7"
										></path>
									</svg>
								</div>
							</div>
							<p class="text-gray-600 mt-3 font-medium text-sm">
								Verificando tu email...
							</p>
						</div>

						<!-- Success/Error state -->
						<div
							v-else-if="shouldVerify"
							class="space-y-4"
						>
							<!-- Email verificado exitosamente -->
							<div
								v-if="data?.status === 204"
								class="space-y-4"
							>
								<!-- Badge de verificación -->
								<div class="bg-green-50 border border-green-200 rounded-lg p-3">
									<div class="flex items-start gap-3">
										<div
											class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center"
										>
											<svg
												class="w-6 h-6 text-white"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="3"
													d="M5 13l4 4L19 7"
												></path>
											</svg>
										</div>
										<div class="flex-1">
											<h2 class="text-lg font-bold text-green-900">
												¡Email verificado!
											</h2>
											<p class="text-green-700 text-xs leading-relaxed mt-0.5">
												Ingresa el código de 6 dígitos enviado a tu correo.
											</p>
										</div>
									</div>
								</div>

								<!-- Formulario OTP Component -->
								<FormOtp ref="formOtpRef" />
							</div>

							<!-- Error al verificar email -->
							<div
								v-else
								class="space-y-3"
							>
								<div class="bg-red-50 border border-red-200 rounded-lg p-3">
									<div class="flex items-start gap-3">
										<div
											class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center"
										>
											<svg
												class="w-6 h-6 text-white"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M6 18L18 6M6 6l12 12"
												></path>
											</svg>
										</div>
										<div class="flex-1">
											<h2 class="text-lg font-bold text-red-900">
												Error al verificar
											</h2>
											<p class="text-red-700 text-xs mt-0.5">
												{{ data?.message || 'El enlace puede haber expirado.' }}
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Invalid URL state -->
						<div
							v-else
							class="text-center py-6"
						>
							<div
								class="w-14 h-14 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3"
							>
								<svg
									class="w-7 h-7 text-yellow-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
									></path>
								</svg>
							</div>
							<h2 class="text-lg font-bold text-gray-900 mb-1">
								Enlace inválido
							</h2>
							<p class="text-gray-600 text-xs mb-4">
								El enlace ha expirado o no es válido.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</NonAuthenticatedLayout>
</template>

<style scoped>
	@keyframes slideIn {
		from {
			opacity: 0;
			transform: translateY(-20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.bg-white {
		animation: slideIn 0.6s ease-out;
	}

	/* Animación de pulse mejorada */
	@keyframes pulse {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.8;
		}
	}
</style>
